import { Subject } from 'rxjs';
import History from '@Services/History';
import Breadcrumb from '@Services/Breadcrumb';
import UserDatabases from '@Services/UserDatabases';
import Deferred from '@Utils/Deferred';
import each from 'lodash/each';
import sortBy from 'lodash/sortBy';
import flatten from 'lodash/flatten';
import some from 'lodash/some';
import find from 'lodash/find';
import filter from 'lodash/filter';
import cloneDeep from 'lodash/cloneDeep';
import store from '@Store';
import { retrieveBreadcrumbs as retrieveBreadcrumbsAction } from '@Containers/Breadcrumbs/BreadcrumbsActions';
import { query as querySelector } from '@Pages/QuickSearch/Components/QuickResults/QuickResultsSelectors';

let resultsInfo = null;
let selectedGroupId = null;
let selectedResultsInfo = null;
let sort = null;
let order = null;

const setDbNames = () => {
  each(resultsInfo, (info) => {
    if (!info) {
      return;
    }
    if (!info.dbName) {
      // eslint-disable-next-line no-param-reassign
      info.dbName = UserDatabases.getDbName(info.databaseId);
    }
  });
};

const updateResultInfoFromBreadcrumb = (resultName, databaseId) => {
  const activeBreadcrumb = Breadcrumb.activateBreadcrumb(resultName, databaseId);
  each(selectedResultsInfo, (resultsInfoItem) => {
    if (resultsInfoItem.databaseId === databaseId && activeBreadcrumb) {
      Object.assign(resultsInfoItem, activeBreadcrumb);
    }
  });
  // Dispatch breadcrumbs redux update
  store.dispatch(retrieveBreadcrumbsAction(databaseId));
};

const sortDatabases = (input, dbNameField) => {
  const sortByNameFn = (info) => info?.[dbNameField]?.toUpperCase();
  let firstPriorityDbs = []; // database with id "0", "7" or "8"
  let secondPriorityDbs = []; // databases with ids 9-14 (must be resorted on label alphanumerically)
  let thirdPriorityDbs = []; // fixed order of databases with ids '5' '15' '1' '16' '18' '17'
  const thirdPriorityDbIds = ['5', '15', '1', '16', '18', '17'];

  // eslint-disable-next-line no-param-reassign
  dbNameField = dbNameField || 'dbName';

  each(input, (info) => {
    if (!info) {
      return;
    }
    if (UserDatabases.isDefaultDatabase(info.databaseId)) {
      firstPriorityDbs.push(info);
    } else {
      // eslint-disable-next-line no-unused-expressions
      thirdPriorityDbIds.indexOf(info.databaseId) !== -1 ? thirdPriorityDbs.push(info) : secondPriorityDbs.push(info);
    }
  });

  firstPriorityDbs = sortBy(firstPriorityDbs, sortByNameFn);
  secondPriorityDbs = sortBy(secondPriorityDbs, sortByNameFn);
  thirdPriorityDbs = flatten(thirdPriorityDbIds.map((i) => filter(input, { databaseId: i })));

  return [...firstPriorityDbs, ...secondPriorityDbs, ...thirdPriorityDbs];
};

const sortResults = () => {
  resultsInfo = sortDatabases(resultsInfo);
};

export const updateResultInfoSortAndOrder = (sortVal, orderVal) => {
  sort = sortVal;
  order = orderVal;
  History.storeRecentHistory(sort, order);
};

const createBreadcrumb = (resultInfo, groupId) => {
  let showStructure = true;
  if (resultInfo.feedbacks) {
    // RXFE-4022: check feedbacks for structure to sync result description for history and breadcrumbs
    showStructure = some(resultInfo.feedbacks, (feedback) => feedback.structure);
  }
  const stateParams = {
    context: resultInfo.context,
    databaseId: resultInfo.databaseId,
    queryId: resultInfo.queryId,
    pageNumber: resultInfo.pageNumber,
    resultName: resultInfo.resultName,
    searchId: resultInfo.searchId,
    description: resultInfo.description,
    hitSetKey: resultInfo.hitSetKey,
    isFromAdvancedSearch: resultInfo.isFromAdvancedSearch,
    showStructure,
    totalResult: resultInfo.totalResult,
    ...(resultInfo.resolvedByCasApi && { resolvedByCasApi: resultInfo.resolvedByCasApi }),
    recordCount: resultInfo.recordCount, // Patent Family Clustering
    semanticSearchIncluded: resultInfo.semanticSearchIncluded, // Reaxys AI Search indication, needed for summary
    semanticResultName: resultInfo.semanticResultName, // Reaxys AI Search indication, needed for summary
    ...(resultInfo.assistantEligible !== undefined && { assistantEligible: resultInfo.assistantEligible }),
    textQuery: resultInfo.textQuery || querySelector(store.getState()), // Reaxys AI Search text query
  };
  return Breadcrumb.createBreadcrumb(stateParams, resultInfo.totalResult, null, true, groupId);
};

export const delayedResultsInfoSubject = new Subject();

export const startLoading = (results) => {
  resultsInfo = results;
  selectedGroupId = null;
  setDbNames();
  sortResults();
};

export const getResults = (resultName, activeDatabase) => {
  if (selectedResultsInfo) {
    // results info per particular db will be restored from breadcrumbs each time when user loads results page
    // it needs for getting only actual breadcrumb info (not last in breadcrumbs chain)
    updateResultInfoFromBreadcrumb(resultName, activeDatabase);
    setDbNames();
  } else {
    const breadcrumbs = Breadcrumb.retrieveActiveBreadcrumbs();
    startLoading(cloneDeep(breadcrumbs));
    selectedResultsInfo = resultsInfo;
    // actually we dont have group id from breadcrumbs but no need to compare it on updates - no updates are expected
    selectedGroupId = true;
    Breadcrumb.updateActiveParams(resultName, activeDatabase);
  }

  return selectedResultsInfo;
};

export const updateInfo = (patch, queryId) => {
  const storedInfo = find(resultsInfo, { queryId });

  // resultInfo can be deleted because of unnecessary context
  if (storedInfo) {
    Object.assign(storedInfo, patch, { totalResult: patch.resultsCount });
    if (selectedGroupId && selectedGroupId === storedInfo.groupId) {
      createBreadcrumb(storedInfo);
    }
  }
};

export const clearResultsInfo = () => {
  resultsInfo = null;
  selectedResultsInfo = null;
  sort = null;
  order = null;
};

export const commitResultsToBreadcrumbs = (groupId) => {
  const result = Deferred();
  // clear resultsInfo from unnecessary contexts from quick search
  selectedResultsInfo = filter(resultsInfo, { groupId });

  // can't use Promise.all here, promises should be resolved one at a time to prevent issues (RXFE-4864)
  (function promiseLoop(i) {
    if (selectedResultsInfo[i]) {
      createBreadcrumb(selectedResultsInfo[i], groupId)
        .then(() => promiseLoop(i + 1))
        .catch(Logger.error);
    } else {
      result.resolve();
    }
  })(0);

  result.promise
    .then(() => {
      selectedGroupId = groupId;
    })
    .catch(Logger.error);

  return result.promise;
};
